import { useEffect, useRef, useState, useCallback } from 'react'
import { Trash2, Star, ImagePlus } from 'lucide-react'
import { photoApi, type Photo } from '../../lib/api'

/**
 * PhotoManager — a reusable photo gallery + uploader for any farm entity (a tree,
 * a specific plant, a piece of equipment, a block, a harvest, …). Drop it into a
 * detail/edit panel with the resource's type + id:
 *
 *   <PhotoManager resourceType="plant" resourceId={plant.id} />
 *   <PhotoManager resourceType="equipment" resourceId={eq.id} />
 *
 * Phone photos are downscaled on a <canvas> before upload; the camera opens
 * directly on mobile (capture="environment"). Images stream back through the
 * authenticated API, so they're blob-fetched and shown via object URLs.
 */

async function downscaleImage(file: File, maxEdge = 1280, quality = 0.82): Promise<Blob> {
  const dataUrl = await new Promise<string>((res, rej) => {
    const r = new FileReader()
    r.onload = () => res(r.result as string)
    r.onerror = rej
    r.readAsDataURL(file)
  })
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image()
    i.onload = () => res(i)
    i.onerror = rej
    i.src = dataUrl
  })
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height))
  const w = Math.round(img.width * scale), h = Math.round(img.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w; canvas.height = h
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)
  return await new Promise<Blob>((res, rej) =>
    canvas.toBlob(b => b ? res(b) : rej(new Error('encode failed')), 'image/jpeg', quality))
}

interface Props {
  resourceType: string
  resourceId: string
  label?: string
}

const iconBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22, borderRadius: 6, border: 'none', cursor: 'pointer',
  background: 'rgba(14,18,9,0.65)', color: '#fff',
}

export function PhotoManager({ resourceType, resourceId, label = 'Photos' }: Props) {
  const [photos, setPhotos] = useState<Photo[]>([])
  const [urls, setUrls] = useState<Record<string, string>>({})
  const urlsRef = useRef<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    if (!resourceId) return
    try {
      const { data } = await photoApi.list(resourceType, resourceId)
      setPhotos(data.photos || [])
    } catch { /* ignore — empty gallery */ }
  }, [resourceType, resourceId])

  useEffect(() => { load() }, [load])

  // Blob-fetch each new photo (authenticated) and cache its object URL.
  useEffect(() => {
    photos.forEach(p => {
      if (!urlsRef.current[p.id]) {
        photoApi.fetchObjectURL(p.id)
          .then(u => { urlsRef.current[p.id] = u; setUrls(prev => ({ ...prev, [p.id]: u })) })
          .catch(() => {})
      }
    })
  }, [photos])

  // Revoke all object URLs on unmount.
  useEffect(() => () => { Object.values(urlsRef.current).forEach(URL.revokeObjectURL) }, [])

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true); setErr(null)
    try {
      const blob = await downscaleImage(file)
      await photoApi.upload(resourceType, resourceId, blob)
      await load()
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
      setErr(msg || 'Upload failed. Try again.')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function del(id: string) {
    try { await photoApi.remove(id) } catch { /* best effort */ }
    if (urlsRef.current[id]) { URL.revokeObjectURL(urlsRef.current[id]); delete urlsRef.current[id] }
    setPhotos(p => p.filter(x => x.id !== id))
  }

  async function star(id: string) {
    try { await photoApi.setPrimary(id); await load() } catch { /* ignore */ }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
          {label}{photos.length > 0 ? ` (${photos.length})` : ''}
        </span>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy || !resourceId}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500,
            padding: '5px 11px', borderRadius: 7, cursor: busy ? 'default' : 'pointer',
            border: '0.5px solid var(--color-border-secondary)',
            background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)',
            opacity: busy || !resourceId ? 0.6 : 1,
          }}
        >
          <ImagePlus size={14} />
          {busy ? 'Uploading…' : 'Add photo'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFile}
          style={{ display: 'none' }}
        />
      </div>

      {err && <div style={{ fontSize: 12, color: 'var(--color-text-danger)', marginBottom: 6 }}>{err}</div>}

      {photos.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', padding: '8px 0' }}>
          No photos yet — add one from your camera or library.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 8 }}>
          {photos.map(p => (
            <div key={p.id} style={{
              position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden',
              background: 'var(--color-background-secondary)',
              border: p.is_primary ? '2px solid #F5A623' : '0.5px solid var(--color-border-tertiary)',
            }}>
              {urls[p.id]
                ? <img src={urls[p.id]} alt={p.caption || 'photo'} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--color-text-tertiary)' }}>…</div>}
              <div style={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 4 }}>
                {!p.is_primary && (
                  <button type="button" title="Make primary" onClick={() => star(p.id)} style={iconBtn}>
                    <Star size={12} />
                  </button>
                )}
                <button type="button" title="Delete" onClick={() => del(p.id)} style={iconBtn}>
                  <Trash2 size={12} />
                </button>
              </div>
              {p.is_primary && (
                <div style={{ position: 'absolute', bottom: 4, left: 4, fontSize: 9, fontWeight: 700, color: '#0E1209', background: '#F5A623', padding: '1px 5px', borderRadius: 4 }}>
                  Primary
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
